import { QueryClient } from '@tanstack/react-query'

/**
 * The thing the old app got wrong: a bare `new QueryClient()` module singleton
 * with default options.
 *
 * `staleTime: 0` means a query is stale the instant it lands, so every mount
 * refetches and any prefetched data is thrown away immediately. The route
 * loaders in `src/routes/` prefetch precisely so a screen can mount with its
 * data already there; a zero stale time would make all of that work decorative.
 *
 * This used to guard a second failure too — a module-level singleton on the
 * SERVER is shared across requests, which leaks one user's cache into another's
 * response — and so returned a fresh client per request there. It also carried
 * a `shouldDehydrateQuery` override, which let PENDING queries cross the wire
 * so a server prefetch could stream instead of being awaited. Neither has
 * anything to act on now: one client for the browser session is the whole
 * story, and a loader that does not await its prefetch simply leaves the query
 * in flight in the cache the components are about to read.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Long enough that prefetched data is trusted on mount and navigating
        // back to a screen is instant, short enough to stay honest.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          const status = (error as { status?: number })?.status
          if (status && status >= 400 && status < 500) return false
          return failureCount < 2
        },
      },
    },
  })
}

let queryClient: QueryClient | undefined

/** One client for the browser session, so the cache survives navigation. */
export function getQueryClient() {
  queryClient ??= makeQueryClient()
  return queryClient
}
